import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormIOData } from "@features/bb-formio";
import {
    CreateMode,
    DisplayMode,
    GridColumnViewSettings,
    GridComponent,
    IGridActionsRowButton,
    IGridColumn,
    IGridSettings,
    ITableSettings,
    UpdateMode
} from "@features/grid";
import { FormioDataViewerService } from "../../services/formioDataViewer.service";
import {
    CountableFilterMatchMode, FilterComponent,
    FilterInputType,
    FilterType,
    IFilterSettings,
    StringFilterMatchMode
} from "@features/filter";
import { OrganizationService } from "@main/companies";
import { UserLogin, UserService } from "@main/users";
import { IOrganization } from "@main/companies/organization";
import { FormIOSurveyService } from "@main/formio/services/formioSurvey.service";
import { ConfirmationService, SelectItem } from "primeng/api";
import { UserHelperService } from "@main/users/user-helper.service";
import { Permissions } from "@main/permissions/permissions";
import { FormIOCategoryService } from "@main/formio/services/formioCategory.service";
import { HistoricalItem } from "@mipro/classes/historical-item";
import { PatientPlaceHolder } from "@main/app-layout/components/patient-switch/patient-placeholder";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { AppStorage } from "@bbwt/utils/app-storage";
import { CoreRole } from "@main/roles";
import { Subject, firstValueFrom, switchMap, takeUntil } from "rxjs";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { MiProModules } from "@mipro/classes/mipro-modules";
import { FormIODataService } from "@features/bb-formio/services/formio-data.service";
import { FormDataPageDTO } from "@features/bb-formio/dto/form-data";
import { RestrictedNhsDataReason } from "@project/medical-record/restricted-nhs-data-reason";

@Component({
    selector: "bbwt-formio-all-instances",
    templateUrl: "./formio-all-instances.component.html",
    styleUrl: "./formio-all-instances.component.scss"
})
export class FormioAllInstancesComponent implements OnInit, OnDestroy {
    header: string = "Form Instances Explorer";

    formId: string | null = null;
    revisionId: string | null = null;

    users: SelectItem[];
    patients: SelectItem[] = [];
    categories: SelectItem[] = [];
    versionsDropdownOptions: SelectItem[] = [];

    selectedFormData: FormIOData[] = [];
    historicalItems: HistoricalItem[] = [];
    globalOrganizationOptions: IOrganization[];

    categoryName: any;
    createdByUserId: string;
    canDelete: boolean = false;
    showHistorical: boolean = false;

    filterSettings: IFilterSettings[];
    formioViewerDataGridSettings: IGridSettings;
    tableSettingsFormioDataViewer: ITableSettings;

    displayFormDialog: boolean = false;
    displaySelectPatientDialog: boolean = false;

    filterVisible: boolean = false;

    private destroyed$: Subject<void> = new Subject<void>();
    private _hasWritePermissionForModule: boolean = false;

    @ViewChild(GridComponent) dataGrid: GridComponent;
    @ViewChild(FilterComponent) filterComponent: FilterComponent;

    protected restrictedNhsReason = RestrictedNhsDataReason.None;
    protected secondaryEmptyMessage = "There are no form instances to show";
    protected loadingData = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private formioDataViewerService: FormioDataViewerService,
        private formIoDataService: FormIODataService,
        private organizationService: OrganizationService,
        private surveyService: FormIOSurveyService,
        private formIOCategoryService: FormIOCategoryService,
        private userService: UserService,
        private userHelperService: UserHelperService,
        private patientService: PatientService,
        private router: Router,
        private broadcastService: BroadcastService
    ) {
        broadcastService
            .on(UserHelperService.ClaimsUpdatedEventName)
            .pipe(
                takeUntil(this.destroyed$),
                switchMap(_ => this.dataGrid && this.dataGrid.reload())
            ).subscribe();

        this.broadcastService
            .on<string>(PatientService.CurrentPatientChangedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(patientId => {
                this.patientChanged(patientId);
            });

        if (this.userHelperService.isTopAdmin) {
            this.organizationService.getAllPlain().then(x => {
                this.globalOrganizationOptions = x;
            });
        } else {
            const user = <UserLogin>this.userService.currentUser;
            this.globalOrganizationOptions = user.userAccesses?.map(x => x.organization) ?? [];
        }
    }

    get currentUserHasWritePermissions(): boolean {
        return this.categoryName && this._hasWritePermissionForModule;
    }

    private patientChanged(patientId: string): void {
        this.setPatientFilterValue(patientId);
    }

    onPatientFilterClear() {
        this.patientService.currentPatientId = null;
    }

    clearFilter() {
        if (!this.userHelperService.isPatient) {
            this.patientService.currentPatientId = null;
        }
    }

    filterInitialized() {
        if (this.patientService.currentPatientId) {
            this.setPatientFilterValue(this.patientService.currentPatientId);
        }

        this.dataGrid?.reload().then();
    }

    setPatientFilterValue(patientId: string): void {
        let patientIdArray = [patientId];
        if (patientId == null) {
            patientIdArray = []
        }
        const newFilterValues: Array<{ propertyName: string; value: any; matchMode?: CountableFilterMatchMode | StringFilterMatchMode }> = [];
        newFilterValues.push({
            propertyName: "patientId",
            value: patientIdArray
        });
        this.filterComponent.setFilterValues(newFilterValues);
    }

    private restoreSelectedPatient(): void {
        const storedPatient: PatientPlaceHolder | null =
            this.userHelperService.isUser &&
            AppStorage.getItem<PatientPlaceHolder>("selectedPatient");

        if (!this.patientService.currentPatientId && storedPatient) {
            const fallbackId = storedPatient.id || storedPatient.patientId;

            if (fallbackId) {
                this.patientService.currentPatientId = fallbackId;
                AppStorage.setItem("selectedPatient", null);
            }
        }
    }

    async ngOnInit(): Promise<void> {
        this.restoreSelectedPatient();

        this.formioViewerDataGridSettings = {
            createMode: CreateMode.Disabled
        }

        this.formioDataViewerService.categoryId = null;
        this.formioDataViewerService.isExplorerPage = true;

        const params = {...(await firstValueFrom(this.activatedRoute.queryParams)), ...(await firstValueFrom(this.activatedRoute.data))};

        this.formId = params["formId"];
        this.revisionId = params["revisionId"];
        this.categoryName = params["category"];
        this.createdByUserId = params["userId"];

        this.formioDataViewerService.formDefinitionId = -1;

        this.categories = (await this.formIOCategoryService.getAllCategories()).map<SelectItem>(x => {
            return {
                value: x.id_original,
                label: x.name
            }
        });

        if (this.categoryName != null) {
            this.header = this.categoryName + ": Folder";
            this._hasWritePermissionForModule = true;
            const permissions = this.formIOCategoryService.getWritePermissionsForCategory(this.categoryName);
            if (permissions) {
                this._hasWritePermissionForModule = (permissions.length === 0 && this.userHelperService.hasPermission(Permissions.WriteFormsInstances))
                    ||(this.userHelperService.hasPermission(...permissions) && this.userHelperService.hasPermission(Permissions.WriteFormsInstances));
            } else  {
                this._hasWritePermissionForModule = this.userHelperService.hasPermission(`${this.categoryName} - Write`);
            }

            this.formioDataViewerService.categoryId = this.categories.find(x => x.label == this.categoryName).value;
        }

        this.canDelete = this.userHelperService.isTopAdmin || this._hasWritePermissionForModule;

        const suggestions = await this.patientService.getAll();
        const patients = suggestions?.patients?.filter(x => !this.isAdmin(x.fullName)) ?? [];
        this.patients = patients.map<SelectItem>(x => {
            return {
                value: x.patientId,
                label: x.fullName
            }
        })

        this.users = (await this.surveyService.getAllUserSuggestions()).map(userItem => <SelectItem>{
            label: userItem.name + " (" + userItem.username + ")",
            value: userItem.id
        })

        this.tableSettingsFormioDataViewer = {
            columns: <IGridColumn[]>[
                {
                    field: "createdOn",
                    header: "Created On",
                    displayMode: DisplayMode.Date,
                    displayDateMomentFormat: "ddd DD/MM/yyyy",
                    viewSettings: new GridColumnViewSettings({ width: "10%" })
                },
                {
                    field: "episodeName",
                    header: "Episode",
                    viewSettings: new GridColumnViewSettings({ width: "25%" }),
                    sortable: false
                },

                {
                     field: "userFullName",
                     header: "Completed By",
                     viewSettings: new GridColumnViewSettings({ width: "20%" })
                 },

                {
                    field: "patientFullName",
                    header: "Patient",
                    viewSettings: new GridColumnViewSettings({ width: "20%" }),
                },
                {
                    field: "version",
                    header: "Version",
                    viewSettings: new GridColumnViewSettings({ width: "5%" })
                },

                {
                    field: "formName",
                    header: "Form Name",
                    viewSettings: new GridColumnViewSettings({ width: "10%" })
                },
            ]
        };

        this.initializeDataGridSettings();
        await this.setGridFilters();
    }

    onGridDeleteHandler(): void {
        this.setGridFilters().then();
    }

    private isAdmin(fullName: string): boolean {
        fullName = this.processFullName(fullName);
        return fullName === this.processFullName(CoreRole.MiProAdmin) || fullName === this.processFullName(CoreRole.SuperAdmin);
    }

    private processFullName(fullName: string): string {
        return fullName.toLowerCase().trim().replace(" ", "");
    }

    private async fillVersionDropdownOptions(): Promise<SelectItem[]> {
        return (this.versionsDropdownOptions = (await this.formioDataViewerService.GetAvailableVersions()).map<SelectItem>(version => {
            return {
                label: version,
                value: version
            };
        }));
    };

    private async setGridFilters(): Promise<void> {
        await this.fillVersionDropdownOptions();

        this.users = (await this.surveyService.getAllUserSuggestions()).map(
            userItem =>
                <SelectItem>{
                    label: userItem.name + " (" + userItem.username + ")",
                    value: userItem.id
                }
        );

        const categoryDefaultValue = !!this.categoryName ? this.categories.find(x => x.label == this.categoryName)?.value : null;

        this.filterSettings = [];
        this.filterSettings = <IFilterSettings[]>[
            {
                header: "Created By (User)",
                valueFieldName: "createdby.id",
                matchModeSelectorVisible: false,
                inputType: FilterInputType.Multiselect,
                dropdownOptions: this.users,
                autoSubmitSelectableInputChange: true,
                disable: () => false // When userId has value disable
            },
            {
                header: "Created On",
                valueFieldName: "createdOn",
                inputType: FilterInputType.Calendar,
                matchModeSelectorVisible: false,
                filterType: FilterType.Date,
                autoSubmitSelectableInputChange: true,
                calendarDateFormat: "dd/mm/yy"
            },
            {
                header: "Version",
                valueFieldName: "version",
                inputType: FilterInputType.Multiselect,
                filterType: FilterType.Text,
                autoSubmitSelectableInputChange: true,
                dropdownOptions: this.versionsDropdownOptions
            },
            {
                header: "Categories",
                valueFieldName: "formDefinition.formCategoryId",
                filterType: FilterType.Numeric,
                inputType: FilterInputType.Dropdown,
                dropdownOptions: this.categories,
                autoSubmitSelectableInputChange: true,
                defaultValue: categoryDefaultValue,
                visible: () => this.categoryName == null
            },
            {
                visible: () => !this.userHelperService.isPatient,
                header: "Patient",
                autoSubmitSelectableInputChange: true,
                valueFieldName: "patientId",
                inputType: FilterInputType.Multiselect,
                dropdownOptions: this.patients
            },
            {
                header: "Show Archived",
                valueFieldName: "showArchived",
                inputType: FilterInputType.Checkbox,
                filterType: FilterType.Boolean,
                autoSubmitSelectableInputChange: true,
            }
        ];
    };

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private initializeDataGridSettings() {
        this.formioViewerDataGridSettings = {
            dataService: this.formioDataViewerService,
            updateMode: UpdateMode.Disabled,
            createMode: !this.currentUserHasWritePermissions ? CreateMode.Disabled : CreateMode.External,
            createFunc: () => this.createNewFormData(),
            deletingEnabled: this.canDelete,
            deletingAvailable: (rowData: any) => !rowData.isArchived,
            rowStyleClassFunc: (rowData: any) => ({
                "archived-instance": rowData.isArchived
            }),
            actionsColumnWidth: "200px",
            additionalRowActions: [
                <IGridActionsRowButton>{
                    hint: "View Data",
                    primeIcon: "pi pi-file",
                    buttonClass: "p-button-rounded p-button-secondary p-button-text",
                    handler: async (data: FormDataPageDTO) => {
                        const queryParams = {
                            formId: data.formDefinitionId,
                            revisionId: data.formRevisionId,
                            formDataId: data.id,
                            forceReadOnly: true
                        };

                        const url = this.router.serializeUrl(
                            this.router.createUrlTree(["app/formio/display"], { queryParams })
                        );

                        this.router.navigateByUrl(url).then();
                    }
                },
                <IGridActionsRowButton>{
                    primeIcon: "mi mi-button-edit",
                    buttonClass: "p-button-rounded p-button-secondary p-button-text",
                    hint: "Update",
                    visible: (data: FormDataPageDTO) => this.canEdit(data),
                    handler: (data: FormDataPageDTO) => {
                        const queryParams = {
                            formId: data.formDefinitionId,
                            revisionId: data.formRevisionId,
                            formDataId: data.id,
                        };
                        const url = this.router.serializeUrl(this.router.createUrlTree(["app/formio/display"], { queryParams }));
                        window.open(url, "_blank");
                    },
                },
                <IGridActionsRowButton>{
                    hint: "History",
                    primeIcon: "pi pi-history",
                    buttonClass: "p-button-rounded p-button-secondary p-button-text",
                    handler: (rowData: FormIOData) => {
                        this.historicalItems = rowData.historical;
                        this.showHistorical = true;
                    }
                },
                <IGridActionsRowButton>{
                    primeIcon: "pi pi-check-circle",
                    buttonClass: "p-button-rounded p-button-secondary p-button-text",
                    hint: "Mark as Completed",
                    visible: (data: FormDataPageDTO) => this.canEdit(data),
                    handler: (data: FormDataPageDTO) => {
                        this.confirmationService.confirm({
                            message: "Are you sure you want mark Form Instance as completed?",
                            accept: async () => {
                                await this.formIoDataService.markAsComplete(data.id);
                                const queryParams = {
                                    formId: data.formDefinitionId,
                                    revisionId: data.formRevisionId,
                                    formDataId: data.id,
                                    generatePdf: true
                                };
                                this.router.navigate(["app/formio/display"], { queryParams }).then();
                            }
                        });
                    },
                }
            ]
        };
    }

    private canEdit(data: FormDataPageDTO){
        return data.isDraft && (this.userHelperService.isTopAdmin || this._hasWritePermissionForModule) &&
        (!data.editableOnlyByInstantiator || data.userId === this.userService.currentUser.id)
    }

    private hasWritePermissionForModule(categoryName: string): boolean {
        const permissionsMap: { [key: string]: () => boolean } = {
            [MiProModules.Consultations]: () =>
                this.userHelperService.hasPermission(
                    Permissions.ConsultationHistoryPrivateEpisodeWrite,
                    Permissions.ConsultationHistoryWrite,
                    Permissions.ConsultationHistoryWritePrivateEpisodesUnrestricted
                ),
            [MiProModules.Prescriptions]: () => this.userHelperService.hasPermission(Permissions.WritePrescriptions),
            [MiProModules.Tues]: () => true,
            [MiProModules.Referrals]: () => true,
            [MiProModules.Investigations]: () => true,
            [MiProModules.FunctionalGut]: () => true,
            [MiProModules.Diary]: () => this.userHelperService.hasPermission(Permissions.WriteDiary)
        };

        // FormioModules
        return permissionsMap[categoryName]
            ? permissionsMap[categoryName]()
            : this.userHelperService.hasPermission(`${categoryName} - Write`);
    }

    private createNewFormData(): void {
        if (!this.patientService.currentPatientId) {
            // If there is no patient selected, force the user to select one.
            this.displaySelectPatientDialog = true;

            // Otherwise, just display the form filling page.
        } else this.displayFormDialog = true;
    }

    protected selectPatientForNewFormData(patientId: string): void {
        this.patientService.currentPatientId = patientId;
        this.displaySelectPatientDialog = false;

        // Display form filling page only if the patient was correctly set.
        this.displayFormDialog = !!this.patientService.currentPatientId;
    }

    protected cancelPatientSelectionForNewFormData(): void {
        this.displaySelectPatientDialog = false;
        this.displayFormDialog = false;
    }

    toggleFilter() {
        this.filterVisible = !this.filterVisible;
    }

    get gridHasData() {
        return this.dataGrid?.getTableProperty("totalRecords") > 0;
    }
}
